// Pure conversions for Il mio feed: the api's feeds (src/api/schema.d.ts)
// to what the panel shows, the editor's values to the body of a call, a
// refused call to its message. No fetching, no React (tests/feeds-adapt.test.ts).
import { isApiError } from "../../api/client";
import type { components } from "../../api/schema";
import type { Source, SubSource } from "../../data/mock";
import { subtreeKeys } from "../news/adapt";

export type UserFeed = components["schemas"]["UserFeed"];
export type UserFeeds = components["schemas"]["UserFeeds"];
export type NewFeed = components["schemas"]["NewFeed"];
export type FeedChange = components["schemas"]["FeedChange"];

/** The feeds in the panel's order: by position, then by creation, then by id (a stable order whatever the api sent). */
export function sortFeeds(feeds: readonly UserFeed[]): UserFeed[] {
  return [...feeds].sort(
    (a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id),
  );
}

/**
 * The value of ?feed= for the virtual folder «Preferiti»: the pinned sources
 * themselves, not a feed of the api. It is the folder of the tab without
 * ?feed= too.
 */
export const FAVOURITES_FOLDER = "preferiti";

/**
 * The feed the tab shows: the one named in ?feed= when it is the user's;
 * null for the folder «Preferiti» (no ?feed=, ?feed=preferiti, a deleted
 * feed, a hand-written id).
 */
export function selectedFeed(feeds: readonly UserFeed[], fromUrl: string | null): UserFeed | null {
  return (fromUrl ? feeds.find((f) => f.id === fromUrl) : undefined) ?? null;
}

/**
 * The tree of the sources panel cut down to the sections whose key is in
 * `keys`: an organisation, a chamber, with the sections it keeps; the ones
 * left with nothing go. Every organisation left is marked favourite: the
 * tree is cut to a feed's sources or to the favourites, both favourites only.
 */
export function restrictTree(active: readonly Source[], keys: Iterable<string>): Source[] {
  const wanted = new Set(keys);
  const cut = (node: SubSource): SubSource | null => {
    if (!node.children || node.children.length === 0) return wanted.has(node.id) ? node : null;
    const children = node.children.map(cut).filter((c): c is SubSource => c !== null);
    return children.length > 0 ? { ...node, children } : null;
  };
  const out: Source[] = [];
  for (const org of active) {
    const children = (org.children ?? []).map(cut).filter((c): c is SubSource => c !== null);
    if (children.length > 0) out.push({ ...org, favorite: true, children });
  }
  return out;
}

/** The keys of the editor's checked sections after a click on a node: its whole subtree in or out. */
export function toggleKeys(selected: readonly string[], node: Source | SubSource, checked: boolean): string[] {
  const keys = subtreeKeys(node);
  const set = new Set(selected);
  for (const key of keys) {
    if (checked) set.add(key);
    else set.delete(key);
  }
  return [...set].sort();
}

/** Whether a name is already the name of another feed, case aside (the api's 409). */
export function nameInUse(feeds: readonly UserFeed[], name: string, exceptId: string | null = null): boolean {
  const wanted = name.trim().toLowerCase();
  return feeds.some((f) => f.id !== exceptId && f.name.trim().toLowerCase() === wanted);
}

/** The name of the virtual folder: no feed may take it (the api's 422 «this name is reserved»). */
export const RESERVED_NAME = "Preferiti";

/** Whether a name is the reserved one, case and blanks aside, as the api compares it. */
export function isReservedName(name: string): boolean {
  return name.trim().toLowerCase() === RESERVED_NAME.toLowerCase();
}

/** Why a name cannot be a feed's: reserved, or another feed's already. */
export type NameProblem = "reserved" | "in-use";

/** The problem a name has before any call, if any: the editor says it and keeps «Salva» disabled. */
export function nameProblem(
  feeds: readonly UserFeed[],
  name: string,
  exceptId: string | null = null,
): NameProblem | null {
  if (isReservedName(name)) return "reserved";
  return nameInUse(feeds, name, exceptId) ? "in-use" : null;
}

const sameKeys = (a: readonly string[], b: readonly string[]) => {
  const x = [...new Set(a)].sort();
  const y = [...new Set(b)].sort();
  return x.length === y.length && x.every((k, i) => k === y[i]);
};

/**
 * The body of PATCH /me/feeds/{id} with only what the editor changed: the
 * name when it differs (trimmed), the sources when the set differs. Null when
 * nothing changed, so nothing is sent.
 */
export function feedChange(
  feed: Pick<UserFeed, "name" | "sources">,
  name: string,
  sources: readonly string[],
): FeedChange | null {
  const change: FeedChange = {};
  const trimmed = name.trim();
  if (trimmed !== feed.name) change.name = trimmed;
  if (!sameKeys(feed.sources, sources)) change.sources = [...new Set(sources)].sort();
  return change.name !== undefined || change.sources !== undefined ? change : null;
}

/** The body of POST /me/feeds from the editor's values. */
export function newFeed(name: string, sources: readonly string[]): NewFeed {
  return { name: name.trim(), sources: [...new Set(sources)].sort() };
}

/** A feed after a change, as the api will answer it: for the optimistic update. */
export function applyFeedChange(feed: UserFeed, change: FeedChange): UserFeed {
  let sources = feed.sources;
  if (change.sources != null) sources = change.sources;
  else if (change.add?.length || change.remove?.length) {
    const remove = new Set(change.remove ?? []);
    sources = [...feed.sources.filter((key) => !remove.has(key)), ...(change.add ?? [])];
  }
  return {
    ...feed,
    ...(change.name != null ? { name: change.name } : {}),
    ...(sources !== feed.sources ? { sources: [...new Set(sources)].sort() } : {}),
  };
}

/** How much of a node a folder holds: every one of its keys, some of them («in parte»), or none. */
export type Membership = "all" | "some" | "none";

/** For each feed, how much of `keys` it holds (none when `keys` is empty): the ticks of the folder chooser. */
export function folderMembership(feeds: readonly UserFeed[], keys: readonly string[]): Record<string, Membership> {
  const of = (feed: UserFeed): Membership => {
    const held = keys.filter((key) => feed.sources.includes(key)).length;
    return held === 0 ? "none" : held === keys.length ? "all" : "some";
  };
  return Object.fromEntries(feeds.map((f) => [f.id, of(f)]));
}

/** The feeds that hold at least one of `keys`: what an unpinned source leaves («Esce anche da: …»). */
export function foldersHolding(feeds: readonly UserFeed[], keys: readonly string[]): UserFeed[] {
  return feeds.filter((f) => keys.some((key) => f.sources.includes(key)));
}

/** The PATCH that puts `keys` into a feed: only the ones it does not hold; null when it holds them all. */
export function addToFolder(feed: Pick<UserFeed, "sources">, keys: readonly string[]): FeedChange | null {
  const add = [...new Set(keys)].filter((key) => !feed.sources.includes(key)).sort();
  return add.length > 0 ? { add } : null;
}

/** The PATCH that takes `keys` out of a feed: only the ones it holds; null when it holds none. */
export function removeFromFolder(feed: Pick<UserFeed, "sources">, keys: readonly string[]): FeedChange | null {
  const remove = [...new Set(keys)].filter((key) => feed.sources.includes(key)).sort();
  return remove.length > 0 ? { remove } : null;
}

/** What was asked of the api: the three actions of the panel, and the two of the pin's folder chooser. */
export type FeedAction = "create" | "change" | "delete" | "folder" | "create-folder";

const FAILED: Record<FeedAction, string> = {
  create: "Creazione del feed non riuscita.",
  change: "Modifica del feed non riuscita.",
  delete: "Eliminazione del feed non riuscita.",
  folder: "Salvataggio nella cartella non riuscito.",
  "create-folder": "Fonte salvata in Preferiti; cartella non creata.",
};

export const NAME_IN_USE = "Nome già usato da un altro feed: scegline un altro.";
export const NAME_RESERVED = "Questo nome è riservato alla cartella Preferiti.";
export const NOT_FAVOURITES = "Alcune fonti non sono più tra i preferiti: scegli di nuovo le fonti.";
export const INVALID = "Dati del feed non validi.";
export const NOT_FOUND = "Feed non trovato: forse è stato eliminato.";

/** The `detail` the api answers a reserved name with. */
const RESERVED_DETAIL = "this name is reserved";

/**
 * What the panel says of a refused call, and whether «Riprova» makes sense:
 * a 409 (the name), a 422 (the reserved name, a source that is not a
 * favourite, or a body the contract refuses) and a 404 are answers, the user
 * changes something; any other failure can be retried as it is.
 */
export function feedErrorMessage(error: unknown, action: FeedAction): { message: string; retry: boolean } {
  if (isApiError(error)) {
    if (error.status === 409) return { message: NAME_IN_USE, retry: false };
    if (error.status === 422) {
      if (error.problem?.detail === RESERVED_DETAIL) return { message: NAME_RESERVED, retry: false };
      const unknown = error.problem?.unknown;
      return { message: Array.isArray(unknown) ? NOT_FAVOURITES : INVALID, retry: false };
    }
    if (error.status === 404) return { message: NOT_FOUND, retry: false };
  }
  return { message: FAILED[action], retry: true };
}
